import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI, meetingsAPI } from '../../services/api';
import './MeetingStatistics.css';

const MeetingStatistics = () => {
  const [stats, setStats] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/admin');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, meetingsData] = await Promise.all([
        adminAPI.getMeetingStats(),
        adminAPI.getMeetings()
      ]);
      setStats(statsData.data);
      setMeetings(meetingsData.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics based on meetings
  const calculateStats = () => {
    if (!meetings.length) return null;

    const now = new Date();
    let filteredMeetings = meetings;

    // Filter by date range
    if (dateRange === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filteredMeetings = meetings.filter(m => new Date(m.createdAt) >= weekAgo);
    } else if (dateRange === 'month') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filteredMeetings = meetings.filter(m => new Date(m.createdAt) >= monthAgo);
    }

    const total = filteredMeetings.length;
    const scheduled = filteredMeetings.filter(m => m.status === 'scheduled').length;
    const completed = filteredMeetings.filter(m => m.status === 'completed').length;
    const inProgress = filteredMeetings.filter(m => m.status === 'in-progress').length;
    const cancelled = filteredMeetings.filter(m => m.status === 'cancelled').length;

    // Calculate average duration for completed meetings
    const completedMeetings = filteredMeetings.filter(m => m.status === 'completed');
    const avgDuration = completedMeetings.length > 0
      ? Math.round(completedMeetings.reduce((acc, m) => acc + (m.duration || 0), 0) / completedMeetings.length)
      : 0;

    // Most active hosts
    const hostCounts = {};
    filteredMeetings.forEach(m => {
      const hostName = m.hostName || 'Unknown';
      hostCounts[hostName] = (hostCounts[hostName] || 0) + 1;
    });
    const topHosts = Object.entries(hostCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    // Meetings by day of week
    const dayCounts = [0, 0, 0, 0, 0, 0, 0];
    filteredMeetings.forEach(m => {
      const day = new Date(m.scheduledStart).getDay();
      dayCounts[day]++;
    });

    return {
      total,
      scheduled,
      completed,
      inProgress,
      cancelled,
      avgDuration,
      topHosts,
      dayCounts,
      filteredMeetings
    };
  };

  const calculatedStats = calculateStats();

  // Format duration
  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="statistics-page">
      <div className="statistics-header">
        <h1>Statistiques des Réunions</h1>
        <p>Analysez les performances des réunions</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Date Range Filter */}
      <div className="date-filter">
        <label>Période:</label>
        <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
          <option value="all">Tout le temps</option>
          <option value="week">7 derniers jours</option>
          <option value="month">30 derniers jours</option>
        </select>
      </div>

      {calculatedStats && (
        <>
          {/* Main Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Réunions</h3>
              <p className="stat-value">{calculatedStats.total}</p>
            </div>
            <div className="stat-card">
              <h3>Planifiées</h3>
              <p className="stat-value">{calculatedStats.scheduled}</p>
            </div>
            <div className="stat-card success">
              <h3>Terminées</h3>
              <p className="stat-value">{calculatedStats.completed}</p>
            </div>
            <div className="stat-card warning">
              <h3>En cours</h3>
              <p className="stat-value">{calculatedStats.inProgress}</p>
            </div>
            <div className="stat-card secondary">
              <h3>Annulées</h3>
              <p className="stat-value">{calculatedStats.cancelled}</p>
            </div>
            <div className="stat-card">
              <h3>Durée Moyenne</h3>
              <p className="stat-value">{formatDuration(calculatedStats.avgDuration)}</p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="charts-row">
            {/* Status Distribution */}
            <div className="chart-card">
              <h3>Répartition par Statut</h3>
              <div className="pie-chart">
                <svg viewBox="0 0 100 100">
                  {calculatedStats.total > 0 && (
                    <>
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="transparent"
                        stroke="#28a745"
                        strokeWidth="20"
                        strokeDasharray={`${(calculatedStats.completed / calculatedStats.total) * 251.2} 251.2`}
                        transform="rotate(-90 50 50)"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="transparent"
                        stroke="#007bff"
                        strokeWidth="20"
                        strokeDasharray={`${(calculatedStats.scheduled / calculatedStats.total) * 251.2} 251.2`}
                        strokeDashoffset={`-${(calculatedStats.completed / calculatedStats.total) * 251.2}`}
                        transform="rotate(-90 50 50)"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="transparent"
                        stroke="#ffc107"
                        strokeWidth="20"
                        strokeDasharray={`${(calculatedStats.inProgress / calculatedStats.total) * 251.2} 251.2`}
                        strokeDashoffset={`-${((calculatedStats.completed + calculatedStats.scheduled) / calculatedStats.total) * 251.2}`}
                        transform="rotate(-90 50 50)"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="transparent"
                        stroke="#dc3545"
                        strokeWidth="20"
                        strokeDasharray={`${(calculatedStats.cancelled / calculatedStats.total) * 251.2} 251.2`}
                        strokeDashoffset={`-${((calculatedStats.completed + calculatedStats.scheduled + calculatedStats.inProgress) / calculatedStats.total) * 251.2}`}
                        transform="rotate(-90 50 50)"
                      />
                    </>
                  )}
                </svg>
              </div>
              <div className="chart-legend">
                <div className="legend-item">
                  <span className="legend-color completed"></span>
                  <span>Terminées ({calculatedStats.completed})</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color scheduled"></span>
                  <span>Planifiées ({calculatedStats.scheduled})</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color in-progress"></span>
                  <span>En cours ({calculatedStats.inProgress})</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color cancelled"></span>
                  <span>Annulées ({calculatedStats.cancelled})</span>
                </div>
              </div>
            </div>

            {/* Day of Week Distribution */}
            <div className="chart-card">
              <h3>Réunions par Jour</h3>
              <div className="bar-chart">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day, index) => (
                  <div className="bar-item" key={day}>
                    <div className="bar-container">
                      <div
                        className="bar-fill"
                        style={{
                          height: `${calculatedStats.dayCounts[index] > 0
                            ? (calculatedStats.dayCounts[index] / Math.max(...calculatedStats.dayCounts)) * 100
                            : 0}%`
                        }}
                      />
                    </div>
                    <span className="bar-label">{day}</span>
                    <span className="bar-value">{calculatedStats.dayCounts[index]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Hosts */}
            <div className="chart-card">
              <h3>Top Hôtes</h3>
              <div className="hosts-list">
                {calculatedStats.topHosts.length > 0 ? (
                  calculatedStats.topHosts.map(([host, count], index) => (
                    <div className="host-item" key={host}>
                      <span className="host-rank">{index + 1}</span>
                      <span className="host-name">{host}</span>
                      <span className="host-count">{count}</span>
                    </div>
                  ))
                ) : (
                  <p className="no-data">Aucune donnée</p>
                )}
              </div>
            </div>
          </div>

          {/* Completion Rate */}
          <div className="completion-rate-card">
            <h3>Taux de Complétion</h3>
            <div className="completion-bar">
              <div
                className="completion-fill"
                style={{
                  width: `${calculatedStats.total > 0
                    ? (calculatedStats.completed / calculatedStats.total) * 100
                    : 0}%`
                }}
              />
            </div>
            <div className="completion-stats">
              <span>
                {calculatedStats.total > 0
                  ? Math.round((calculatedStats.completed / calculatedStats.total) * 100)
                  : 0}% des réunions terminées
              </span>
              <span>
                {calculatedStats.total > 0
                  ? Math.round((calculatedStats.cancelled / calculatedStats.total) * 100)
                  : 0}% annulées
              </span>
            </div>
          </div>

          {/* Recent Meetings Table */}
          <div className="recent-meetings">
            <h3>Réunions Récentes</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Titre</th>
                    <th>Hôte</th>
                    <th>Date</th>
                    <th>Durée</th>
                    <th>Statut</th>
                    <th>Participants</th>
                  </tr>
                </thead>
                <tbody>
                  {calculatedStats.filteredMeetings
                    .slice(0, 10)
                    .map(meeting => (
                      <tr key={meeting.id}>
                        <td>{meeting.title}</td>
                        <td>{meeting.hostName}</td>
                        <td>{new Date(meeting.scheduledStart).toLocaleDateString('fr-FR')}</td>
                        <td>{meeting.duration} min</td>
                        <td>
                          <span className={`status-badge ${meeting.status}`}>
                            {meeting.status === 'scheduled' ? 'Planifiée' :
                             meeting.status === 'completed' ? 'Terminée' :
                             meeting.status === 'in-progress' ? 'En cours' : 'Annulée'}
                          </span>
                        </td>
                        <td>{meeting.invitees?.length || 0}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MeetingStatistics;
